// FLAG:useDocuAi — the confirmation the PO asked for: before a "review again" save is written,
// tell the user in concrete terms what it will change — WHICH expense is re-derived, which one is
// booked and therefore left alone — and ask the one question the server cannot answer for them:
// "Amazon EU S.à r.l." → "Amazon EU Sarl" is a correction, "Amazon" → "UPS" is a different company,
// and nothing in the data distinguishes the two. Rendered on the re-review path — and, since T13 of
// plan omxweb-draft-receipt-instead-of-review-row (2026-09-17), on a FIRST review as well, but there
// only for that one question (`mode='firstReview'`): the form arms `updateDirectory` on the first
// edit of a linked record, and without the question the save renamed the supplier silently
// (dev supplier 97849 → "Baustoff+Metall GmbH2"). A first review that does not diverge saves
// exactly as before, without this dialog.
import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import lang from '@/lang'
import { ReviewPayeeRole, ReviewPayeeState } from '@/modules/receipt/review/review-payee-state'

type ReviewSavePlan = {
  action: 'create' | 'update' | 'skip'
  reason: string | null
  id: number | null
  state: string | null
  receiptNumber: string | null
  // FLAG:useDocuAi — FIXED CONTRACT with getReviewSaveEffects: once the linked receipt has left the
  // editable states, the save drops the reviewed payee AND the reviewed amounts rather than writing
  // half a change, and the preview projects exactly these two flags onto the plan carrying the
  // reason `expenseNotDraft` / `revenueNotDraft`. For an editable receipt they are ABSENT, never
  // `false` — so read them as truth, not as presence of the key.
  payeeIgnored?: boolean
  amountsIgnored?: boolean
}

type ReviewReceiptTransition = {
  action: 'none' | 'switch' | 'blocked'
  reason: 'documentOnlyRequiresRemoval' | 'sourceNotEditable' | 'sourceHasMultipleDocuments' | 'targetAlreadyExists' | 'sourceAccessDenied' | 'targetAccessDenied' | null
  source: {
    type: 'expense' | 'revenue'
    id: number
    state: string
    financeApiId: string | null
    receiptNumber: string | null
  } | null
  target: 'expense' | 'revenue' | 'document'
}

export type ReviewSaveEffects = {
  reviewStatus: string | null
  // FLAG:useDocuAi — 'expense' | 'revenue': WHERE this save books. The server returns it from the
  // same branch that creates the receipt; the dialog does NOT derive it itself (binding rule from
  // plan `usedocuai-sonstige-einnahmen`: two sources for the same decision are guaranteed to
  // diverge).
  target?: 'expense' | 'revenue' | 'document'
  transition?: ReviewReceiptTransition
  expense: ReviewSavePlan
  // Populated only when this save books to the revenue side.
  revenue?: ReviewSavePlan | null
  party: {
    linked: {
      type: 'supplier' | 'customer'
      role: ReviewPayeeRole
      id: number
      name: string
    } | null
    snapshotName: string
    identityMatches: boolean | null
    roleMatches: boolean
  } | null
}

// The same words the review form's role tabs use — never the jargon "master record". Two forms,
// because German declines these: `nom` for a subject ("the linked supplier …"), `n` for the
// n-declension form that serves both accusative and dative ("the supplier …").
const ROLE_WORDS: Record<ReviewPayeeRole, { nom: string, n: string }> = lang.documents.reReview.roleWords

// What the user decided about a payee whose name no longer matches the linked master record.
export type PartyResolution = 'update' | 'create' | 'keep'

// 'reReview'    — the full preview: ledger effects + the party question (the original dialog).
// 'firstReview' — ONLY the party question; a first review has no "what changes" to announce, and
//                 the invalid-e-invoice warning is asked by `continueSave` itself on that path.
export type ReviewConfirmMode = 'reReview' | 'firstReview'

export const normalizePartyName = (value?: string | null) => `${value || ''}`.trim().replace(/\s+/g, ' ').toLowerCase()

// The name currently in the FORM — not the persisted snapshot the server read. The divergence
// that matters is "what the user just typed" vs "the master record this document is linked to".
export const getEditedPayeeName = (reviewPayee?: ReviewPayeeState | null) => {
  if (!reviewPayee) return ''
  return reviewPayee.role === 'privateCustomer'
    ? [reviewPayee.salutation, reviewPayee.title, reviewPayee.firstName, reviewPayee.lastName].filter(Boolean).join(' ')
    : [reviewPayee.companyName, reviewPayee.companyNameAffix].filter(Boolean).join(' ')
}

// FLAG:useDocuAi — ONE rule for "does the typed name still match the linked master record", shared
// by this dialog (which asks the question) and useReReviewSave (which decides whether a FIRST review
// has to ask it at all). Two copies of the comparison would be two ways to disagree.
//
// A ROLE SWITCH is not a question about names. The server describes the STORED snapshot (still
// attached to the supplier here), while the form now says "customer" — the old master record is then
// irrelevant, and "correct the supplier, it was a typo" would make no sense:
// according to the review, the receipt belongs to a different party in a different role.
// `changeReviewPayeeRole` already discarded directoryRef during the switch, so save resolves
// normally in the NEW role (search, match, or create). The dialog therefore uses the same language
// as for an initial review.
export const resolveLinkedPartyDivergence = (
  effects: ReviewSaveEffects | null | undefined,
  reviewPayee?: ReviewPayeeState | null,
) => {
  const editedName = getEditedPayeeName(reviewPayee)
  const serverLinked = effects?.party?.linked ?? null
  const roleSwitched = !!serverLinked && !!reviewPayee?.role && serverLinked.role !== reviewPayee.role
  const linked = roleSwitched ? null : serverLinked
  const hasDivergedIdentity = !!linked && !!normalizePartyName(editedName)
    && normalizePartyName(linked.name) !== normalizePartyName(editedName)
  return { editedName, linked, hasDivergedIdentity }
}

// The expense sentence, straight from the server's plan — same rule the save executes.
const describeExpense = (expense: ReviewSaveEffects['expense']) => {
  const { reReview } = lang.documents
  if (expense.action === 'update') return reReview.expenseUpdate(expense.receiptNumber)
  if (expense.action === 'create') return reReview.expenseCreate

  switch (expense.reason) {
    case 'expenseNotDraft': return reReview.expenseBooked(expense.receiptNumber)
    case 'cancellationNotSupported': return reReview.expenseCancellation
    case 'missingExpenseAccess': return reReview.expenseNoAccess
    // FLAG:useDocuAi — switching ledgers. Without a dedicated sentence, this case fell through to
    // "no expense will be created either": literally true and still misleading because a receipt is in
    // fact created — just on the other side. The line below (describeRevenue) says which one.
    case 'belongsToRevenue': return reReview.expenseBelongsToRevenue
    default: return reReview.expenseNone
  }
}

// FLAG:useDocuAi — the revenue half, from the same server plan as the expense half.
const describeRevenue = (revenue: ReviewSavePlan) => {
  const { reReview } = lang.documents
  if (revenue.action === 'update') return reReview.revenueUpdate(revenue.receiptNumber)
  if (revenue.action === 'create') return reReview.revenueCreate

  switch (revenue.reason) {
    case 'revenueNotDraft': return reReview.revenueBooked(revenue.receiptNumber)
    case 'missingRevenueAccess': return reReview.revenueNoAccess
    default: return reReview.revenueNone
  }
}

// FLAG:useDocuAi — what the save SILENTLY drops when the receipt is locked. The row above already
// says the receipt stays as it is; this line says which reviewed values do not survive the save, so
// the user does not go looking for them in the booked receipt afterwards. Both flags travel with
// the lock reason, but each is evaluated on its own truth: a future payload that ignores only one
// of the two must not make the dialog claim both.
const describeIgnoredFields = (plan: ReviewSavePlan | null | undefined, role: string): string | undefined => {
  const { reReview } = lang.documents
  const payeeIgnored = plan?.payeeIgnored === true
  const amountsIgnored = plan?.amountsIgnored === true

  if (payeeIgnored && amountsIgnored) return reReview.ignoredPayeeAndAmounts(role)
  if (payeeIgnored) return reReview.ignoredPayee(role)
  if (amountsIgnored) return reReview.ignoredAmounts
  return undefined
}

const describeTransition = (transition: ReviewReceiptTransition) => {
  if (!transition.source) return ''
  const { reReview } = lang.documents
  const source = transition.source.type === 'expense' ? 'Ausgabe' : 'Einnahme'
  const target = transition.target === 'expense' ? 'Ausgabe' : transition.target === 'revenue' ? 'Einnahme' : 'Nur Dokument'
  const number = transition.source.receiptNumber

  if (transition.action === 'switch') return reReview.transitionSwitch(source, number, target)
  if (transition.reason === 'sourceNotEditable') return reReview.transitionSourceNotEditable(source, number, target)
  if (transition.reason === 'sourceHasMultipleDocuments') return reReview.transitionSourceHasMultipleDocuments(source, number)
  if (transition.reason === 'targetAlreadyExists') return reReview.transitionTargetAlreadyExists(source, number, target)
  if (transition.reason === 'sourceAccessDenied') return reReview.transitionSourceAccessDenied(source, number)
  if (transition.reason === 'targetAccessDenied') return reReview.transitionTargetAccessDenied(target)
  return reReview.transitionDocumentOnlyRequiresRemoval(source, number)
}

// One selectable option row. The indicator is drawn with plain CSS instead of MUI's <Radio>:
// a legacy app-wide stylesheet zeroes the switch-base padding and clips the icon SVG, which
// rendered the radios as a stray ")" glyph. Fixing that globally would touch every radio in the
// app; here the decision is important enough to deserve full-width rows anyway. The native
// <input type='radio'> stays (visually hidden inside the <label>) so keyboard, form semantics and
// screen readers keep working — only the visual is ours.
const PartyResolutionOption = ({ value, label, selected, onSelect }: {
  value: PartyResolution
  label: string
  selected: boolean
  onSelect: (value: PartyResolution) => void
}) => (
  <Box
    component='label'
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      px: 1.5,
      py: 1.25,
      mb: 1,
      cursor: 'pointer',
      borderRadius: 1,
      border: '1px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      backgroundColor: selected ? 'action.selected' : 'transparent',
      '&:hover': { borderColor: selected ? 'primary.main' : 'text.disabled' },
    }}
  >
    <input
      type='radio'
      name='reReviewPartyResolution'
      value={value}
      checked={selected}
      onChange={() => onSelect(value)}
      style={{ position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0 }}
    />
    <Box
      aria-hidden
      sx={{
        flex: '0 0 auto',
        mt: '2px',
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main' }} />}
    </Box>
    <Typography variant='body2'>{label}</Typography>
  </Box>
)

const ReReviewConfirmDialog = ({
  open,
  effects,
  isLoading,
  hasError,
  reviewPayee,
  invalidEInvoice: invalidEInvoiceVerdict = false,
  mode = 'reReview',
  onCancel,
  onConfirm,
}: {
  open: boolean
  effects: ReviewSaveEffects | null
  isLoading: boolean
  hasError: boolean
  reviewPayee?: ReviewPayeeState | null
  mode?: ReviewConfirmMode
  // FLAG:useDocuAi — the KoSIT/format verdict. It belongs HERE, not in a second modal behind this
  // one: "review again" already asks a question, and two consecutive confirmations lead to the
  // second being dismissed without reading. This dialog does not exist for an INITIAL review;
  // `continueSave` (receipt-edit.tsx) asks the same question by itself there.
  invalidEInvoice?: boolean
  onCancel: () => void
  onConfirm: (partyResolution: PartyResolution | null) => void
}) => {
  const { reReview } = lang.documents
  const [partyResolution, setPartyResolution] = useState<PartyResolution | null>(null)
  const isFirstReview = mode === 'firstReview'
  // The verdict is shown here only on a re-review: on a first review `continueSave`
  // (receipt-edit.tsx) asks that question itself, and it must be asked exactly once.
  const invalidEInvoice = invalidEInvoiceVerdict && !isFirstReview

  const { editedName, linked, hasDivergedIdentity } = useMemo(
    () => resolveLinkedPartyDivergence(effects, reviewPayee),
    [effects, reviewPayee],
  )

  // Two different roles, deliberately kept apart: sentences about the LINKED record use its own
  // role, while "will be created" uses the role the reviewer picked in the form — after a role
  // switch those genuinely differ ("linked to the supplier …" → "create as a new private customer").
  const linkedRole = ROLE_WORDS[linked?.role ?? 'supplier']
  const formRole = ROLE_WORDS[reviewPayee?.role ?? 'supplier']

  // A fresh dialog must never carry a previous answer into a new save.
  useEffect(() => {
    if (open) setPartyResolution(null)
  }, [open])

  // A first review announces no ledger effects, so a transition verdict has nothing to block here —
  // the save itself still applies its own rules exactly as it did before this dialog existed.
  const transitionBlocked = !isFirstReview && effects?.transition?.action === 'blocked'
  const canConfirm = !isLoading && !hasError && !transitionBlocked && (!hasDivergedIdentity || partyResolution !== null)
  const bookingTargetNotice = () => {
    if (effects?.transition?.action === 'blocked') {
      return (
        <Alert severity='error' sx={{ mb: 1.5 }} data-qs-id='reReviewBookingTargetBlocked'>
          {describeTransition(effects.transition)}
        </Alert>
      )
    }
    if (effects?.transition?.action === 'switch') {
      return (
        <Alert severity='warning' sx={{ mb: 1.5 }} data-qs-id='reReviewBookingTargetSwitch'>
          {describeTransition(effects.transition)}
          <Typography component='span' display='block' sx={{ mt: 1 }}>
            {reReview.transitionTaxReportingWarning}
          </Typography>
        </Alert>
      )
    }
    if (effects?.target === 'revenue') {
      return (
        <Alert severity='info' sx={{ mb: 1.5 }} data-qs-id='reReviewBookingTargetSwitch'>
          {reReview.targetRevenue}
        </Alert>
      )
    }
    return null
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth data-qs-id='reReviewConfirmDialog' data-review-mode={mode}>
      <DialogTitle>{isFirstReview ? reReview.partyTitle(linkedRole.nom) : reReview.title}</DialogTitle>
      <DialogContent>
        {/* First and independent of the effects fetch: the verdict is a fact about the receipt, not a
            consequence of this save — it must not wait for the preview to load before appearing.    */}
        {invalidEInvoice && (
          <Alert severity='error' sx={{ mb: 1.5 }} data-qs-id='reReviewInvalidEInvoice'>
            {lang.documents.invalidEInvoiceSave.alert}
          </Alert>
        )}
        {isLoading && <Typography>{reReview.loading}</Typography>}
        {hasError && <Alert severity='error'>{reReview.loadError}</Alert>}
        {!isLoading && !hasError && effects && (
          <>
            {/* The ledger effects belong to a RE-review only: a first review has nothing to
                re-derive, so the dialog carries just the party question there.              */}
            {!isFirstReview && <Typography sx={{ mb: 1 }}>{reReview.intro}</Typography>}
            {/* FLAG:useDocuAi — show the ledger switch first and highlighted: it is the only effect
                that moves the row out of the list where the user is looking for it. `target` comes
                from getReviewSaveEffects, not from a derivation here.                   */}
            {!isFirstReview && bookingTargetNotice()}
            {!isFirstReview && !transitionBlocked && <List dense disablePadding>
              {effects.target !== 'revenue' && (
                <ListItem disableGutters>
                  <ListItemText
                    primary={effects.target === 'document' ? lang.documents.reviewBookingTarget.documentOnlyEffect : describeExpense(effects.expense)}
                    // "Nur Dokument" books nothing, so nothing can be ignored there either — the
                    // qualifier belongs to the expense sentence alone.
                    secondary={effects.target === 'document' ? undefined : describeIgnoredFields(effects.expense, formRole.nom)}
                    data-qs-id='reReviewExpenseEffect'
                  />
                </ListItem>
              )}
              {effects.revenue && (
                <ListItem disableGutters>
                  <ListItemText
                    primary={describeRevenue(effects.revenue)}
                    secondary={describeIgnoredFields(effects.revenue, formRole.nom)}
                    data-qs-id='reReviewRevenueEffect'
                  />
                </ListItem>
              )}
              {!linked && !!editedName && (
                <ListItem disableGutters>
                  <ListItemText primary={reReview.partyWillCreate(formRole.nom, editedName)} />
                </ListItem>
              )}
            </List>}

            {!transitionBlocked && hasDivergedIdentity && linked && (
              <Box sx={{ mt: isFirstReview ? 0 : 2 }} data-testid='re-review-party-diverged'>
                <Alert severity='warning' sx={{ mb: 1.5 }}>
                  {reReview.partyDivergedIntro(linkedRole.n, linked.name, editedName)}
                </Alert>
                <Box role='radiogroup' data-qs-id='reReviewPartyResolution'>
                  {([
                    ['update', reReview.partyChoiceUpdate(linkedRole.n, linked.name)],
                    ['create', reReview.partyChoiceCreate(formRole.n, editedName)],
                    ['keep', reReview.partyChoiceKeep(linkedRole.n, linked.name)],
                  ] as Array<[PartyResolution, string]>).map(([value, label]) => (
                    <PartyResolutionOption
                      key={value}
                      value={value}
                      label={label}
                      selected={partyResolution === value}
                      onSelect={setPartyResolution}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{reReview.cancel}</Button>
        <Button
          variant='contained'
          disabled={!canConfirm}
          data-qs-id='reReviewConfirmButton'
          onClick={() => onConfirm(hasDivergedIdentity ? partyResolution : null)}
        >
          {invalidEInvoice ? lang.documents.invalidEInvoiceSave.confirm : reReview.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReReviewConfirmDialog
